import './utils/translationGuard'
import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import './index.css'
// Initialize i18n before any component uses useTranslation
import './i18n'
import { errorRingBuffer } from './utils/errorRingBuffer'
import { recordAuthEvent, recordBoot } from './utils/authJournal'

recordBoot()

// Initialize Sentry before anything else
import { initSentry, isSentryEnabled } from './utils/sentry.js'
import ErrorBoundary from './components/common/ErrorBoundary.jsx'

// Auto-reload on dynamic chunk load failures (stale deploy race condition).
// Throttled to one reload per 10s via sessionStorage to prevent infinite loops.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  if (
    reason instanceof TypeError &&
    typeof reason.message === 'string' &&
    (reason.message.includes('Failed to fetch dynamically imported module') ||
      reason.message.includes('Importing a module script failed'))
  ) {
    event.preventDefault();
    const key = 'grid_chunk_reload_at';
    const last = sessionStorage.getItem(key);
    if (!last || Date.now() - parseInt(last, 10) > 10_000) {
      sessionStorage.setItem(key, String(Date.now()));
      recordAuthEvent('reload', { reason: 'chunk-load-failure', withStack: false });
      window.location.reload();
    }
  }
});

// Suppress Chrome extension errors (Google Workspace, etc.)
// These errors come from browser extensions injecting code, not from our app
const suppressExtensionErrors = () => {
  const originalError = window.console.error;
  const originalWarn = window.console.warn;
  
  window.console.error = (...args) => {
    const errorMessage = args.join(' ').toLowerCase();
    const errorString = args.join(' ');
    
    // Suppress Chrome extension i18n errors and HubSpot script errors
    if (
      errorMessage.includes('i18n.text called before ready') ||
      errorMessage.includes('sidepaneltoggle') ||
      errorMessage.includes('sidepaneltoggle.label') ||
      errorMessage.includes('sidepaneltoggle') ||
      errorString.includes('sidePanelToggle') ||
      errorMessage.includes('go/i18n-help') ||
      (errorMessage.includes('vm') && errorMessage.includes('everywhere.js')) ||
      errorString.includes('everywhere.js') ||
      errorMessage.includes('hubspot.define included more than once') ||
      errorMessage.includes('quick-fetch') ||
      errorMessage.includes('quickfetchscript')
    ) {
      // Silently ignore these extension/external script errors
      return;
    }
    
    originalError.apply(console, args);
    const errorMsg = args.map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) return `${a.message} ${a.stack || ''}`;
      if (a && typeof a === 'object' && 'message' in a) return String(a.message);
      try { return JSON.stringify(a); } catch { return String(a); }
    }).join(' ');
    if (!isKnownNoise(errorMsg)) {
      errorRingBuffer.push({ ts: new Date().toISOString(), level: 'error', msg: errorMsg });
    }
  };
  
  window.console.warn = (...args) => {
    const warnMessage = args.join(' ').toLowerCase();
    const warnString = args.join(' ');
    
    // Suppress Chrome extension warnings
    if (
      warnMessage.includes('i18n.text called before ready') ||
      warnMessage.includes('sidepaneltoggle') ||
      warnMessage.includes('sidepaneltoggle.label') ||
      warnString.includes('sidePanelToggle') ||
      warnMessage.includes('go/i18n-help') ||
      // Suppress HubSpot script warnings (from browser extensions or external scripts)
      warnMessage.includes('hubspot.define included more than once') ||
      warnMessage.includes('quick-fetch') ||
      warnMessage.includes('quickfetchscript') ||
      warnMessage.includes('everywhere.js') ||
      warnString.includes('everywhere.js')
    ) {
      return;
    }

    originalWarn.apply(console, args);
    const warnMsg = args.map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) return `${a.message} ${a.stack || ''}`;
      if (a && typeof a === 'object' && 'message' in a) return String(a.message);
      try { return JSON.stringify(a); } catch { return String(a); }
    }).join(' ');
    if (!isKnownNoise(warnMsg)) {
      errorRingBuffer.push({ ts: new Date().toISOString(), level: 'warn', msg: warnMsg });
    }
  };
  
  // Also suppress unhandled promise rejections from extensions
  window.addEventListener('unhandledrejection', (event) => {
    const errorMessage = event.reason?.message?.toLowerCase() || 
                        event.reason?.toString()?.toLowerCase() || '';
    const errorString = event.reason?.message || event.reason?.toString() || '';
    
    if (
      errorMessage.includes('i18n.text called before ready') ||
      errorMessage.includes('sidepaneltoggle') ||
      errorMessage.includes('sidepaneltoggle.label') ||
      errorString.includes('sidePanelToggle') ||
      errorMessage.includes('go/i18n-help') ||
      errorString.includes('everywhere.js')
    ) {
      event.preventDefault(); // Suppress the error
    }
  });
  
  // Also suppress uncaught errors from extensions (catch-all)
  window.addEventListener('error', (event) => {
    const errorMessage = event.message?.toLowerCase() || '';
    const errorString = event.message || '';
    const filename = event.filename?.toLowerCase() || '';
    
    if (
      errorMessage.includes('i18n.text called before ready') ||
      errorMessage.includes('sidepaneltoggle') ||
      errorMessage.includes('sidepaneltoggle.label') ||
      errorString.includes('sidePanelToggle') ||
      errorMessage.includes('go/i18n-help') ||
      filename.includes('everywhere.js') ||
      errorString.includes('everywhere.js')
    ) {
      event.preventDefault(); // Suppress the error
      return false;
    }
  }, true); // Use capture phase to catch errors early
};

// Suppress extension errors immediately
suppressExtensionErrors();

// Known noise patterns that should never appear in bug reports.
// These are infrastructure-level events, not app errors.
const NOISE_PATTERNS = [
  'securetoken.googleapis.com',
  'accounts.google.com',
  'access-control-allow-origin',
  'err_failed',
  'net::err_failed',
  'error loading image',
  'auth/network-request-failed',
  'auth/internal-error',
  'cross-origin',
  'cors policy',
  'preflight request',
];

function isKnownNoise(msg) {
  const lower = (msg || '').toLowerCase();
  return NOISE_PATTERNS.some((p) => lower.includes(p));
}

window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message || String(event.reason) || 'Unhandled rejection';
  const stack = event.reason?.stack || '';
  const code = event.reason?.code || '';
  if (isKnownNoise(msg) || isKnownNoise(stack) || isKnownNoise(code)) return;
  errorRingBuffer.push({ ts: new Date().toISOString(), level: 'unhandled', msg, stack });
});

// Capture uncaught runtime errors in the error ring buffer.
// Using addEventListener instead of window.onerror= to avoid being overwritten by Sentry.
window.addEventListener('error', (event) => {
  const msg = event.message || String(event.error?.message || '');
  const source = event.filename || '';
  if (isKnownNoise(msg) || isKnownNoise(source)) return;
  errorRingBuffer.push({
    ts: new Date().toISOString(),
    level: 'error',
    msg: msg.slice(0, 500),
    url: source,
    stack: event.error?.stack?.slice(0, 1000),
  });
});

// Initialize Sentry asynchronously
const initializeApp = async () => {
  try {
    // Initialize Sentry first
    await initSentry();
    
    // Check if Sentry is enabled and log status
    const sentryEnabled = await isSentryEnabled();
    
    // Show environment information
    console.log(`🚀 BT4SAP Frontend inicializado`);
    console.log(`🌐 API URL: ${import.meta.env.VITE_API_URL}`);
    console.log(`🔧 Environment: ${import.meta.env.VITE_ENV || 'development'}`);
    console.log(`⚙️ Production mode: ${import.meta.env.MODE === 'production'}`);
    console.log(`📊 Sentry enabled: ${sentryEnabled}`);
    
    // Render the app
    ReactDOM.createRoot(document.getElementById('root')).render(
      <React.StrictMode>
        <ErrorBoundary name="App">
          <App />
        </ErrorBoundary>
      </React.StrictMode>,
    );
  } catch (error) {
    console.error('❌ Failed to initialize app:', error);

    // Fallback: render app without Sentry
    ReactDOM.createRoot(document.getElementById('root')).render(
      <React.StrictMode>
        <ErrorBoundary name="App">
          <App />
        </ErrorBoundary>
      </React.StrictMode>,
    );
  }
};

// Initialize the app
initializeApp();